/**
 * AdMockupMedia Component
 *
 * Media display component with platform-specific aspect ratios
 */

import React, { useCallback, useState } from 'react';
import { cn } from '@/lib/utils';
import { AdMockupMediaProps } from '../types/base.types';

export const AdMockupMedia: React.FC<AdMockupMediaProps> = ({
  mediaUrl,
  aspectRatio = 'auto',
  platform,
  alt = 'Ad media',
  className,
  showPlayButton = false,
}) => {
  const aspectRatioClasses = {
    '1:1': 'aspect-square',
    '4:5': 'aspect-[4/5]',
    '16:9': 'aspect-video',
    '9:16': 'aspect-[9/16]',
    'auto': '',
  };

  // All platforms use object-cover since images are pre-cropped to the correct aspect ratio
  const objectFitClass = 'object-cover';

  const [imgError, setImgError] = useState(false);

  // Detect if URL is a video based on file extension
  const isVideo = /\.(mp4|webm|ogg|mov|avi)$/i.test(mediaUrl);

  // Tall aspect ratios (9:16) can cause lazy-loaded images to never trigger
  // the Intersection Observer if the container is partially off-screen.
  // Use eager loading for these to avoid intermittent blank images.
  const useLazy = aspectRatio !== '9:16';

  const handleImgError = useCallback(() => {
    setImgError(true);
    console.warn(`[AdMockupMedia] Image failed to load for ${platform}:`, mediaUrl);
  }, [mediaUrl, platform]);

  return (
    <div className={cn(
      'relative bg-gray-100 dark:bg-card-darkAlt overflow-hidden',
      aspectRatioClasses[aspectRatio],
      className
    )}>
      {isVideo ? (
        <>
          <video
            src={mediaUrl}
            className={`w-full h-full ${objectFitClass}`}
            controls
            preload="metadata"
          />
          {/* Play button overlay - only show for video thumbnails without native controls */}
          {showPlayButton && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
              <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                <svg className="w-8 h-8 text-gray-900 ml-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                </svg>
              </div>
            </div>
          )}
        </>
      ) : imgError ? (
        <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500 p-4 text-center text-xs">
          Image failed to load
        </div>
      ) : (
        <img
          src={mediaUrl}
          alt={alt}
          className={`w-full h-full ${objectFitClass}`}
          loading={useLazy ? 'lazy' : 'eager'}
          onError={handleImgError}
        />
      )}
    </div>
  );
};

export default AdMockupMedia;
